import { Typography } from "@suraasa/placebo-ui"
import { useQuery } from "@tanstack/react-query"
import api from "api"
import { queries } from "api/queries"
import { ReattemptType } from "api/resources/assessments/types"
import { AssignmentList } from "api/resources/assignments/types"
import { DeliveryMode } from "api/resources/learningItems/types"
import HandHoldingPen from "assets/Fallback/hand-holding-pen.svg"
import clsx from "clsx"
import Fallback from "components/Fallback"
import ItemList from "components/ItemList"
import ItemRow from "components/ItemRow"
import TruncatedText from "components/TruncatedText"
import CheckRestrictions, {
  getRestriction,
} from "features/AccessManagement/CheckRestrictions"
import {
  buildAssignmentTitle,
  getActionConfig,
} from "features/Assignments/utils"
import ReattemptCount from "features/ReattemptCount"
import { Lock } from "iconoir-react"
import groupBy from "lodash/groupBy"
import { useMemo } from "react"
import { Link, useParams } from "react-router-dom"
import { learningItemSlugs } from "utils/constants"
import { useEnrollments } from "utils/hooks/useEnrollments"

import { useCourseDetails } from ".."
import styles from "./Assignments.module.css"
import GroupedAssignmentCard from "./GroupedAssignments"
import TutorialVideo from "./TutorialVideo"
import { getAssignmentTutorialVideoUrl } from "./TutorialVideo/utils"
import { getAssignmentRowState, getDueDate, ViewAction } from "./utils"

const groupAssignmentsByName = (assignmentList: AssignmentList[]) => {
  const groupByName = groupBy(
    assignmentList,
    item => item.groupName ?? item.shortTitle
  )
  const allGroups = Object.entries(groupByName).map(([groupName, items]) => ({
    groupName,
    assignments: items || [],
  }))
  return allGroups
}

const CourseAssignments = () => {
  // const [openReattemptDialog, setOpenReattemptDialog] = useState<{
  //   title: string
  //   userReattemptCounter?: UserReattemptCounterType | null
  // } | null>(null)
  const { course, planner } = useCourseDetails()

  const { data, isLoading, isError } = useQuery({
    queryKey: queries.assignments.list("course", course.id).queryKey,
    queryFn: () =>
      api.assignments.list({
        urlParams: {
          learningItemType: "course",
          learningItemId: course.id,
        },
      }),
  })

  const groupedAssignments = useMemo(
    () => groupAssignmentsByName(data || []),
    [data]
  )

  const { slug, parentSlug } = useParams() as {
    slug: string
    parentSlug?: string
  }

  const isRestrictedInAnyWay = data?.some(x => getRestriction(x))
  const hasGroupedAssignments = data?.some(item => item.groupName)

  const reAttemptRequestsConfig = data?.find(
    item => item.reattemptType === ReattemptType.ON_REQUEST
  )

  const showNeutralResultTags =
    planner.deliveryAttributes?.assignmentPassingRequired === false

  const {
    enrollments,
    pgctl: { deliveryMode, isNewDeliveryMode },
  } = useEnrollments()

  const isPgCTLCourse = enrollments
    ?.find(x => x.slug === slug)
    ?.parents?.map(x => x.slug)
    .includes(learningItemSlugs.pgctl)

  const hideDueDate = Boolean(
    isPgCTLCourse &&
      deliveryMode &&
      [DeliveryMode.PROFESSIONAL, DeliveryMode.STARTER].includes(deliveryMode)
  )

  const assignmentTutorialVideoUrl = getAssignmentTutorialVideoUrl({
    courseSlug: course.slug,
    deliveryMode: deliveryMode,
    isNewDeliveryMode: isNewDeliveryMode,
  })

  if (isError || isLoading) {
    return (
      <Fallback
        data={{
          image: HandHoldingPen,
          title: "Something went wrong!",
          description: "Please contact care@suraasa.com",
        }}
        className="my-9 sm:my-20 md:my-18"
        hasError={isError}
        isLoading={isLoading}
      />
    )
  }

  if (groupedAssignments.length === 0 && data?.length === 0) {
    return (
      <Fallback
        data={{
          image: HandHoldingPen,
          title: "No skill evidences (assignments) found!",
          description:
            "Make progress in your courses to unlock skill evidences (assignments)!",
        }}
        className="my-9 sm:my-20 md:my-18"
      />
    )
  }

  return (
    <>
      <div className="flex flex-col sm:grid sm:grid-cols-3 sm:gap-2 md:gap-3 lg:gap-4">
        <div
          className={clsx(
            assignmentTutorialVideoUrl && !isRestrictedInAnyWay
              ? "col-span-2"
              : "col-span-3"
          )}
        >
          <Typography className="mb-2 block sm:hidden" variant="title3">
            Skill Evidences (Assignments)
          </Typography>

          {!isLoading && !isError && !isRestrictedInAnyWay && (
            <TutorialVideo
              videoUrl={assignmentTutorialVideoUrl}
              courseId={course.id}
              courseSlug={course.slug}
              courseName={course.name}
              className="mb-2 block sm:hidden"
            />
          )}

          <ReattemptCount
            userReattemptCounter={reAttemptRequestsConfig?.userReattemptCounter}
            className="mb-2"
          />

          {hasGroupedAssignments &&
            groupedAssignments.map((item, index) => (
              <div key={index}>
                <GroupedAssignmentCard
                  hideDueDate={hideDueDate}
                  assignments={item.assignments}
                  courseId={course.id}
                  groupName={item.groupName}
                  showNeutralResultTags={showNeutralResultTags}
                />
              </div>
            ))}

          {!hasGroupedAssignments && (data || []).length > 0 && (
            <ItemList>
              {(data || []).map(assignment => {
                const config = getActionConfig(assignment, {
                  showNeutralResultTags,
                })
                const { isLocked, to } = getAssignmentRowState(
                  assignment,
                  config,
                  { slug }
                )

                return (
                  <ItemRow
                    key={assignment.uuid}
                    component={to ? Link : "div"}
                    to={to}
                    state={to ? { slug, parentSlug } : undefined}
                    className={clsx("relative", {
                      "bg-[linear-gradient(270deg,#FBF0F0_0%,rgba(251,240,240,0)_39.36%)]":
                        config.isOverdue,
                      [styles.draft]: config.isDraft,
                    })}
                    infoSlot={
                      <div className="grow">
                        {config.isOverdue && (
                          <div
                            className={clsx(
                              "absolute left-0 top-0 h-full w-0.5 bg-critical-500"
                            )}
                          />
                        )}
                        {config.isDraft && (
                          <Typography
                            variant="smallBody"
                            style={{ fontSize: 12 }}
                            className="mb-0.5 text-onSurface-500"
                          >
                            Continue Writing
                          </Typography>
                        )}
                        <TruncatedText
                          maxLength={60}
                          variant="strong"
                          className="mb-0.5"
                          hideActionButton
                        >
                          {buildAssignmentTitle(assignment)}
                        </TruncatedText>

                        <div className="flex items-center gap-1">
                          {config.tag}
                          {!hideDueDate && getDueDate(assignment)}
                        </div>
                      </div>
                    }
                    actionSlot={
                      <CheckRestrictions
                        accessStatus={assignment.accessStatus}
                        render={({ isRestricted }) => {
                          if (isLocked || isRestricted) return <Lock />

                          return (
                            <span className="max-sm:self-end">
                              <ViewAction />
                            </span>
                          )
                        }}
                      />
                    }
                  />
                )
              })}
            </ItemList>
          )}
        </div>
        {!isLoading && !isError && !isRestrictedInAnyWay && (
          <TutorialVideo
            videoUrl={assignmentTutorialVideoUrl}
            courseId={course.id}
            courseSlug={course.slug}
            courseName={course.name}
            className="hidden sm:block"
          />
        )}
      </div>
    </>
  )
}

export default CourseAssignments
